import axios from 'axios';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toErrorMessage } from '../api/client';
import { digitsOnly } from '../utils/validation';
import * as endpoints from '../api/endpoints';
import type { CurrentUser, TwoFactorMethod } from '../api/types';
import { useAuth } from '../auth/AuthContext';

/** The API's machine-readable error code, e.g. EMAIL_NOT_VERIFIED. */
function errorCode(error: unknown): string | null {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { code?: string } | undefined;
    return body?.code ?? null;
  }
  return null;
}

/** Port of Pages/Account/Login.cshtml. */
export function LoginPage() {
  const { signIn, completeTwoFactor, isAuthenticated, initialising } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  // Signed in with the right password but the address is not confirmed yet.
  const [unverified, setUnverified] = useState(false);

  // Second step - the code texted to the user's phone or emailed to their address.
  const [challenge, setChallenge] = useState<{ id: string; method: TwoFactorMethod; sentTo: string } | null>(
    null,
  );
  const [code, setCode] = useState('');

  // Set by the signup flow when an account is not active yet (deposit failed, or still being
  // set up) - web shows the same text from TempData["SignupError"].
  const notice = (location.state as { notice?: string } | null)?.notice ?? null;
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'Sign In - InertiaID';
  }, []);

  // Already authenticated - the Razor page redirected to the dashboard here too.
  if (!initialising && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  function goOnwards(signedIn: CurrentUser) {
    const { from, fromUserId } =
      (location.state as { from?: string; fromUserId?: number | null } | null) ?? {};
    // Return to the previous page only for the account that was on it. A different account
    // (signing out of one and into another) starts at the dashboard instead of a page it may
    // not be allowed to open.
    const sameAccount = fromUserId == null || fromUserId === signedIn.id;
    navigate(from && from !== '/login' && sameAccount ? from : '/dashboard', { replace: true });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);
    setInfoMessage(null);
    setUnverified(false);
    setSubmitting(true);

    try {
      const step = await signIn(email, password, rememberMe);
      if (step.kind === 'twoFactor') {
        setChallenge({ id: step.challenge, method: step.method, sentTo: step.sentTo });
        setCode('');
        setInfoMessage(step.message ?? `We sent a 6-digit code to ${step.sentTo}.`);
        return;
      }
      goOnwards(step.user);
    } catch (error) {
      if (errorCode(error) === 'EMAIL_NOT_VERIFIED') setUnverified(true);
      setErrorMessage(toErrorMessage(error, 'Incorrect email or password.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!challenge) return;
    setErrorMessage(null);
    setSubmitting(true);
    try {
      goOnwards(await completeTwoFactor(challenge.id, code, rememberMe));
    } catch (error) {
      // Too many wrong codes or an expired challenge: back to the password step.
      if (errorCode(error) === 'TWO_FACTOR_FAILED') {
        setChallenge(null);
        setPassword('');
      }
      setErrorMessage(toErrorMessage(error, 'That code is not correct.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function resendCode() {
    if (!challenge) return;
    setErrorMessage(null);
    try {
      const next = await endpoints.resendTwoFactorCode(challenge.id);
      setChallenge({ id: next.challenge, method: next.method, sentTo: next.sentTo });
      setInfoMessage(next.message ?? `We sent a new code to ${next.sentTo}.`);
    } catch (error) {
      setChallenge(null);
      setErrorMessage(toErrorMessage(error, 'Please sign in again.'));
    }
  }

  async function emailInstead() {
    if (!challenge) return;
    setErrorMessage(null);
    try {
      const next = await endpoints.emailCodeInstead(challenge.id);
      setChallenge({ id: next.challenge, method: next.method, sentTo: next.sentTo });
      setCode('');
      setInfoMessage(next.message ?? `We emailed a code to ${next.sentTo}.`);
    } catch (error) {
      setChallenge(null);
      setErrorMessage(toErrorMessage(error, 'Please sign in again.'));
    }
  }

  async function resendVerification() {
    setErrorMessage(null);
    try {
      const response = await endpoints.resendVerification(email);
      setUnverified(false);
      setInfoMessage(response.message ?? 'We sent a new confirmation link.');
    } catch (error) {
      setErrorMessage(toErrorMessage(error, 'Could not send the link. Please try again.'));
    }
  }

  if (challenge) {
    return (
      <>
        <div className="card shadow-sm border-0">
          <div className="card-body p-4">
            <h5 className="fw-semibold mb-1">Two-step sign-in</h5>
            <p className="text-body-secondary small mb-4">
              {challenge.method === 'authenticator' ? (
                'Open your authenticator app and enter the 6-digit code shown for InertiaID (or IdVerify, if you set it up before October 2026).'
              ) : (
                <>
                  Enter the 6-digit code we {challenge.method === 'email' ? 'emailed' : 'texted'} to{' '}
                  {challenge.sentTo}.
                  {challenge.method === 'email' && ' If it is not in your inbox, check your spam folder.'}
                </>
              )}
            </p>

            {infoMessage && !errorMessage && (
              <div className="alert alert-info py-2 mb-3 small" role="status">{infoMessage}</div>
            )}
            {errorMessage && (
              <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3" role="alert">
                <i className="bi bi-exclamation-circle-fill flex-shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            <form onSubmit={handleCode} noValidate>
              <input
                className="form-control form-control-lg text-center mb-3"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                {...digitsOnly}
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
              <button
                type="submit"
                className="btn btn-primary w-100 fw-medium"
                disabled={submitting || code.length !== 6}
              >
                {submitting ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                Verify and sign in
              </button>
            </form>

            <div className="d-flex justify-content-between mt-3 small">
              {challenge.method !== 'authenticator' && (
                <button type="button" className="btn btn-link btn-sm p-0" onClick={() => void resendCode()}>
                  Send a new code
                </button>
              )}
              {challenge.method !== 'email' && (
                <button type="button" className="btn btn-link btn-sm p-0" onClick={() => void emailInstead()}>
                  Email me a code instead
                </button>
              )}
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-body-secondary"
                onClick={() => {
                  setChallenge(null);
                  setErrorMessage(null);
                  setInfoMessage(null);
                }}
              >
                Use a different account
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="card shadow-sm border-0">
        <div className="card-body p-4">
          <h5 className="fw-semibold mb-1">Welcome back</h5>
          <p className="text-body-secondary small mb-4">Sign in to your account to continue</p>

          {notice && !errorMessage && (
            <div className="alert alert-warning d-flex align-items-center gap-2 py-2 mb-3" role="alert">
              <i className="bi bi-info-circle-fill flex-shrink-0" />
              <span>{notice}</span>
            </div>
          )}

          {infoMessage && !errorMessage && (
            <div className="alert alert-success d-flex align-items-center gap-2 py-2 mb-3" role="status">
              <i className="bi bi-check-circle-fill flex-shrink-0" />
              <span>{infoMessage}</span>
            </div>
          )}

          {errorMessage && (
            <div className="alert alert-danger py-2 mb-3" role="alert">
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-exclamation-circle-fill flex-shrink-0" />
                <span>{errorMessage}</span>
              </div>
              {unverified && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger mt-2"
                  onClick={() => void resendVerification()}
                >
                  Send the confirmation link again
                </button>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-3">
              <label className="form-label fw-medium" htmlFor="emailInput">
                Email address
              </label>
              <div className="input-group">
                <span className="input-group-text bg-transparent">
                  <i className="bi bi-envelope text-body-secondary" />
                </span>
                <input
                  id="emailInput"
                  type="email"
                  className="form-control border-start-0"
                  placeholder="you@company.com"
                  autoFocus
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="form-label fw-medium mb-0" htmlFor="passwordInput">
                  Password
                </label>
                <Link to="/forgot-password" className="small text-decoration-none" tabIndex={-1}>
                  Forgot password?
                </Link>
              </div>
              <div className="input-group">
                <span className="input-group-text bg-transparent">
                  <i className="bi bi-lock text-body-secondary" />
                </span>
                <input
                  id="passwordInput"
                  type={showPassword ? 'text' : 'password'}
                  className="form-control border-start-0"
                  placeholder="********"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="input-group-text bg-transparent border-start-0"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i
                    className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'} text-body-secondary`}
                  />
                </button>
              </div>
            </div>

            <div className="mb-4">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <label className="form-check-label text-body-secondary small" htmlFor="rememberMe">
                  Keep me signed in for 30 days
                </label>
              </div>
            </div>

            <button type="submit" className="btn btn-primary w-100 fw-medium" disabled={submitting}>
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" /> Signing in...
                </>
              ) : (
                <>
                  <i className="bi bi-box-arrow-in-right me-1" /> Sign In
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      <p className="text-center text-body-secondary small mt-3 mb-1">
        Don&apos;t have an account?{' '}
        <Link to="/signup" className="text-decoration-none">
          Sign up free
        </Link>
      </p>
      <p className="text-center text-body-secondary small mb-0">
        <Link to="/" className="text-body-secondary text-decoration-none">
          <i className="bi bi-arrow-left me-1" /> Back to home
        </Link>
      </p>
    </>
  );
}
